import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RolesResponse, RoleType, RoleClaimsResponse } from '~/Services/AdminRolesService'
import { IProductClaim } from '~/Services/Product.interface'

export interface RolesReducerStore {
  listRolesData: RolesResponse[] | null
  loading: boolean
  success: boolean
  error?: string | null
  rolesUserID: string[]
  rolesUserIDUserId: string | null
  userRoles: {
    [userId: string]: string[]
  }
  modalState: {
    [key: string]: boolean
  }
  roleSelected: Partial<RolesResponse>
  // Full product claim catalog (not Service-Principal-scoped — a Role's
  // claims apply regardless of which SP the role is later assigned to).
  productClaims: { [productId: string]: IProductClaim[] }
  loadingProductClaims: { [productId: string]: boolean }
  isCreatingRole: boolean
  isDeletingRole: boolean
  roleClaims?: RoleClaimsResponse
  isLoadingRoleClaims: boolean
  // Edit-screen save flow: rename (if changed) + replace claims, then flag
  // success so the component can navigate back — mirrors Store/ApiKey's
  // apiKeyUpdated pattern.
  isSavingRole: boolean
  roleSaved: boolean
  isRenamingRole: boolean
}

const initialState: RolesReducerStore = {
  listRolesData: null,
  loading: false,
  success: false,
  error: undefined,
  rolesUserID: [],
  rolesUserIDUserId: null,
  userRoles: {},
  modalState: {},
  roleSelected: {},
  productClaims: {},
  loadingProductClaims: {},
  isCreatingRole: false,
  isDeletingRole: false,
  isLoadingRoleClaims: false,
  isSavingRole: false,
  roleSaved: false,
  isRenamingRole: false,
}

export const RolesSlice = createSlice({
  name: 'Roles',
  initialState,
  reducers: {
    getRolesRequest: (_state, _action: PayloadAction<RoleType | undefined>) => {},
    setRolesSuccess: (state, action) => {
      state.listRolesData = action.payload
    },
    setRolesError: state => {
      state.listRolesData = null
    },
    setModalState: (
      state,
      action: PayloadAction<{ name: string; value: boolean }>,
    ) => {
      const { name, value = false } = action.payload
      state.modalState[name] = value
    },
    setSystemRoleSelected: (
      state,
      action: PayloadAction<Partial<RolesResponse>>,
    ) => {
      state.roleSelected = action.payload
    },
    addRolesRequest: (
      state,
      _action: PayloadAction<{ roleName: string; type: RoleType }>,
    ) => {
      state.isCreatingRole = true
    },
    addRoleSuccess: state => {
      state.isCreatingRole = false
    },
    addRoleError: state => {
      state.isCreatingRole = false
    },
    updateRoleRequest: (
      state,
      _action: PayloadAction<{
        roleName: string
        newRolename: string
        type: RoleType
      }>,
    ) => {
      state.isRenamingRole = true
    },
    updateRoleSuccess: state => {
      state.isRenamingRole = false
    },
    updateRoleError: state => {
      state.isRenamingRole = false
    },

    deleteRoleRequest: (
      state,
      _action: PayloadAction<{ roleName: string; type: RoleType }>,
    ) => {
      state.isDeletingRole = true
    },
    deleteRoleSuccess: state => {
      state.isDeletingRole = false
    },
    deleteRoleError: state => {
      state.isDeletingRole = false
    },
    getProductClaims: (state, action: PayloadAction<string>) => {
      state.loadingProductClaims[action.payload] = true
    },
    getProductClaimsSuccess: (
      state,
      action: PayloadAction<{ productId: string; claims: IProductClaim[] }>,
    ) => {
      const { productId, claims } = action.payload
      state.productClaims[productId] = claims
      state.loadingProductClaims[productId] = false
    },
    getProductClaimsFail: (state, action: PayloadAction<string>) => {
      state.loadingProductClaims[action.payload] = false
    },
    getRoleClaims: (state, _action: PayloadAction<string>) => {
      state.isLoadingRoleClaims = true
    },
    getRoleClaimsSuccess: (state, action: PayloadAction<RoleClaimsResponse>) => {
      state.roleClaims = action.payload
      state.isLoadingRoleClaims = false
    },
    getRoleClaimsFail: state => {
      state.isLoadingRoleClaims = false
    },
    resetRoleClaims: state => {
      state.roleClaims = undefined
    },
    saveRole: (
      state,
      _action: PayloadAction<{
        roleId: string
        roleName: string
        newRolename: string
        type: RoleType
        productClaimIds: string[]
      }>,
    ) => {
      state.isSavingRole = true
      state.roleSaved = false
    },
    saveRoleSuccess: state => {
      state.isSavingRole = false
      state.roleSaved = true
    },
    saveRoleFail: state => {
      state.isSavingRole = false
    },
    resetRoleSaved: state => {
      state.roleSaved = false
    },
    updateUserRoles: (state, action) => {
      state.loading = true
    },
    updateUserRoleFailure(state, action: PayloadAction<string>) {
      state.loading = false
      state.success = false
      state.error = action.payload
    },
    getUserRolesRequest: (state, action: PayloadAction<string>) => {
      state.loading = true
      state.error = null
    },
    getUserRolesSuccess: (
      state,
      action: PayloadAction<{ userId: string; roles: string[] }>,
    ) => {
      state.loading = false
      state.rolesUserID = action.payload.roles
      state.rolesUserIDUserId = action.payload.userId
    },
    getUserRolesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    deleteUserRolesRequest: (
      state,
      action: PayloadAction<{ userId: string; roleName: string[] }>,
    ) => {},
    deleteUserRolesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
  },
})

export const RolesActions = RolesSlice.actions
export const RolesReducer = RolesSlice.reducer
